import { viewTransition } from '@theme/utilities';
import { ThemeEvents } from '@theme/events';

const SKELETON_ITEM_CLASS = 'product-grid__item--skeleton';
const LOADING_CLASS = 'product-grid--loading';
const TEMPLATE_ID = 'product-card-skeleton-template';
const MAX_SKELETON_COUNT = 12;
const DEFAULT_SKELETON_COUNT = 8;
const MIN_SKELETON_MS = 280;

/**
 * @param {Element | Document} root
 * @returns {HTMLTemplateElement | null}
 */
function getSkeletonTemplate(root = document) {
  const template = root.querySelector(`#${TEMPLATE_ID}`);
  return template instanceof HTMLTemplateElement ? template : null;
}

/**
 * @param {HTMLElement} grid
 * @returns {number}
 */
function getSkeletonCount(grid) {
  const visibleCards = grid.querySelectorAll(
    `.product-grid__item:not(.${SKELETON_ITEM_CLASS}):not([hidden])`
  ).length;

  if (visibleCards > 0) {
    return Math.min(visibleCards, MAX_SKELETON_COUNT);
  }

  const mobileColumns = Number.parseInt(grid.style.getPropertyValue('--mobile-columns'), 10) || 2;
  return Math.min(mobileColumns * 4, MAX_SKELETON_COUNT);
}

/**
 * @typedef {object} SkeletonOptions
 * @property {boolean} [replace] - Hide existing cards (filter reload). Default true.
 */

/**
 * @param {HTMLElement} grid
 * @param {number} [count]
 * @param {SkeletonOptions} [options]
 */
export function showProductCardSkeletons(grid, count = DEFAULT_SKELETON_COUNT, options = {}) {
  if (!(grid instanceof HTMLElement)) return;

  const { replace = true } = options;

  hideProductCardSkeletons(grid, { replace });

  const template = getSkeletonTemplate(grid.closest('.shopify-section') ?? document);
  if (!template) return;

  const skeletonCount = Math.min(count || getSkeletonCount(grid), MAX_SKELETON_COUNT);

  for (let i = 0; i < skeletonCount; i += 1) {
    const item = document.createElement('li');
    item.className = `product-grid__item ${SKELETON_ITEM_CLASS}`;
    item.setAttribute('aria-hidden', 'true');
    item.appendChild(template.content.cloneNode(true));
    grid.appendChild(item);
  }

  if (replace) {
    grid.classList.add(LOADING_CLASS);
    grid.setAttribute('aria-busy', 'true');
  }
}

/**
 * @param {HTMLElement} grid
 * @param {SkeletonOptions} [options]
 */
export function hideProductCardSkeletons(grid, options = {}) {
  if (!(grid instanceof HTMLElement)) return;

  const { replace = true } = options;

  grid.querySelectorAll(`.${SKELETON_ITEM_CLASS}`).forEach((item) => item.remove());

  if (replace) {
    grid.classList.remove(LOADING_CLASS);
    grid.removeAttribute('aria-busy');
  }
}

/**
 * Waits until startViewTransition has registered transition.finished.
 * @returns {Promise<void | undefined>}
 */
async function waitForViewTransitionFinished() {
  await new Promise((resolve) => queueMicrotask(resolve));

  for (let i = 0; i < 60; i += 1) {
    if (viewTransition.current) {
      await viewTransition.current;
      return;
    }
    await new Promise((resolve) => requestAnimationFrame(resolve));
  }
}

/**
 * @returns {Promise<void>}
 */
function waitMinimumDisplay() {
  return new Promise((resolve) => {
    window.setTimeout(resolve, MIN_SKELETON_MS);
  });
}

/** @type {WeakMap<HTMLElement, number>} */
const loadingGenerations = new WeakMap();

/**
 * @param {HTMLElement} grid
 */
async function runFilterLoadingCycle(grid) {
  const generation = (loadingGenerations.get(grid) ?? 0) + 1;
  loadingGenerations.set(grid, generation);

  const startedAt = Date.now();
  showProductCardSkeletons(grid, getSkeletonCount(grid), { replace: true });

  try {
    await Promise.all([waitForViewTransitionFinished(), waitMinimumDisplay()]);
  } finally {
    const elapsed = Date.now() - startedAt;
    if (elapsed < MIN_SKELETON_MS) {
      await new Promise((resolve) => window.setTimeout(resolve, MIN_SKELETON_MS - elapsed));
    }

    if (loadingGenerations.get(grid) === generation) {
      hideProductCardSkeletons(grid, { replace: true });
    }
  }
}

function bindFilterLoading() {
  document.addEventListener(ThemeEvents.FilterUpdate, () => {
    requestAnimationFrame(() => {
      document.querySelectorAll('results-list .product-grid').forEach((grid) => {
        if (grid instanceof HTMLElement) {
          runFilterLoadingCycle(grid);
        }
      });
    });
  });
}

function bindInfiniteScrollLoading() {
  document.addEventListener('product-grid:loading', (event) => {
    const grid = event.detail?.grid;
    if (!(grid instanceof HTMLElement)) return;
    showProductCardSkeletons(grid, Math.min(getSkeletonCount(grid), 4), { replace: false });
  });

  document.addEventListener('product-grid:loaded', async (event) => {
    const grid = event.detail?.grid;
    if (!(grid instanceof HTMLElement)) return;

    await waitMinimumDisplay();
    hideProductCardSkeletons(grid, { replace: false });
  });
}

function bindListeners() {
  if (window.__productCardSkeletonBound) return;
  window.__productCardSkeletonBound = true;
  bindFilterLoading();
  bindInfiniteScrollLoading();
}

function init() {
  bindListeners();
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init, { once: true });
} else {
  init();
}
